import { getTestVariant } from '@bridebook/toolbox/src/ab-testing/lib/selectors';
import { useMarket } from '@bridebook/web/app-shared/lib/i18n/hooks/use-market';
import { useABTestIntegration } from '@bridebook/web/lib/ab-tests/hooks/use-ab-test-integration';
import { IApplicationState } from '@bridebook/web/lib/types';
import { useSelector } from '@bridebook/web/lib/utils';
import { CountryCodes } from '@bridebook/toolbox/src/gazetteer';
import { useSupplierPricingGroupedByYear } from '@bridebook/web/lib/supplier/hooks/query/use-supplier';
import {
  hasPricing,
  isPricingModular,
  isPricingVerified,
} from '@bridebook/toolbox/src/supplier/utils/pricing';

const testId = 'LIVE-25246_PriceEstimate';

/**
 * Returns the active variant of the test for use in selectors, components etc.
 * If the test is not activated, it returns 'control'
 */
export const getVariant_PriceEstimate = (state: IApplicationState) => getTestVariant(state, testId);

/**
 * Hook for initializing the test
 */
export const useABTest_PriceEstimate = () => {
  const market = useMarket();
  const triggerCondition = market.country === CountryCodes.GB;
  useABTestIntegration({ testId, triggerCondition });
};

export const usePriceEstimateToolBasicData = ({
  supplierId,
  isVenue,
  supplierCountry,
}: {
  supplierId?: string;
  isVenue?: boolean;
  supplierCountry?: CountryCodes;
}) => {
  const LIVE_25246_Variant = useSelector(getVariant_PriceEstimate);
  const disable = LIVE_25246_Variant === 'control';
  const { data: supplierPricingGroupedByYear, isLoading: isSupplierPricingCountLoading } =
    useSupplierPricingGroupedByYear(
      {
        supplierId,
      },
      { disable },
    );

  // Packages repeat across years, which none of the checks below care about.
  const prices = Object.values(supplierPricingGroupedByYear ?? {}).flat();

  const hasSupplierPricing =
    !!isVenue &&
    !!supplierCountry &&
    supplierCountry === CountryCodes.GB &&
    !isSupplierPricingCountLoading &&
    hasPricing(prices);

  const isVerified = isPricingVerified(prices);
  const isPricingStructureModular = isPricingModular(prices);

  const isEnabled = !disable && hasSupplierPricing;

  return {
    isEnabled,
    hasSupplierPricing,
    isVerified,
    isPricingStructureModular,
    isLoading: isSupplierPricingCountLoading,
  };
};
